import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Autocomplete } from '@react-google-maps/api';
import TopMenu from '../../contexts/TopMenu';
import '../../styles/AddWarehouse.css';
import { API_BASE_URL } from '../../config';

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: 20.5937,
  lng: 78.9629
};

function EditWarehouse({ email, onLogout }) {
  const navigate = useNavigate();
  const { warehouseId } = useParams();
  const [marker, setMarker] = useState(null);
  const [searchBox, setSearchBox] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: localStorage.getItem('userEmail') || '',
    address: '',
    address_2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    operationHoursStart: '',
    operationHoursEnd: '',
    pocName: '',
    pocPhone: '',
    pocEmail: '',
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlace, setSelectedPlace] = useState(null);

  // Fetch warehouse data
  useEffect(() => {
    const fetchWarehouseData = async () => {
      try {
        // Get auth credentials from localStorage
        const authKey = localStorage.getItem('authKey') || '';
        const userId = localStorage.getItem('userId') || '';
        let warehouse = null;
        
        // First try with POST and auth credentials
        if (authKey && userId) {
          try {
            const postResponse = await fetch(`${API_BASE_URL}/brands/warehouses`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                auth_key: authKey,
                user_id: userId
              })
            });
            
            const postResult = await postResponse.json();
            
            if (postResponse.ok && Array.isArray(postResult.data)) {
              warehouse = postResult.data.find(w => w.warehouse_id === warehouseId);
              if (warehouse) {
                setFormData({
                  name: warehouse.name,
                  email: email || warehouse.email || localStorage.getItem('userEmail') || '',
                  address: warehouse.address,
                  address_2: warehouse.address_2 || '',
                  city: warehouse.city,
                  state: warehouse.state,
                  zip: warehouse.zip,
                  country: warehouse.country,
                  operationHoursStart: warehouse.operationHoursStart,
                  operationHoursEnd: warehouse.operationHoursEnd,
                  pocName: warehouse.pocName,
                  pocPhone: warehouse.pocPhone,
                  pocEmail: warehouse.pocEmail,
                  latitude: warehouse.latitude,
                  longitude: warehouse.longitude,
                });
                setMarker({
                  lat: warehouse.latitude,
                  lng: warehouse.longitude
                });
                setSelectedPlace({ formatted_address: warehouse.address });
                setIsLoading(false);
                return;
              }
            }
          } catch (postError) {
            console.error('POST request failed, trying GET:', postError);
          }
        }
        
        // Fall back to GET with email parameter
        if (!warehouse && email) {
          const getResponse = await fetch(`${API_BASE_URL}/brands/warehouses?email=${encodeURIComponent(email)}`);
          const getResult = await getResponse.json();
          
          if (getResponse.ok && Array.isArray(getResult.data)) {
            warehouse = getResult.data.find(w => w.warehouse_id === warehouseId);
            if (warehouse) {
              setFormData({
                name: warehouse.name,
                email: warehouse.email,
                address: warehouse.address,
                address_2: warehouse.address_2 || '',
                city: warehouse.city,
                state: warehouse.state,
                zip: warehouse.zip,
                country: warehouse.country,
                operationHoursStart: warehouse.operationHoursStart,
                operationHoursEnd: warehouse.operationHoursEnd,
                pocName: warehouse.pocName,
                pocPhone: warehouse.pocPhone,
                pocEmail: warehouse.pocEmail,
                latitude: warehouse.latitude,
                longitude: warehouse.longitude,
              });
              setMarker({
                lat: warehouse.latitude,
                lng: warehouse.longitude
              });
              setSelectedPlace({ formatted_address: warehouse.address });
            } else {
              setError('Warehouse not found');
              navigate('/warehouses');
            }
          } else {
            throw new Error(getResult.message || 'Failed to fetch warehouse data');
          }
        } else if (!warehouse) {
          setError('Warehouse not found');
          navigate('/warehouses');
        }
      } catch (err) {
        setError('Failed to fetch warehouse data. Please try again later.');
        console.error('Error fetching warehouse:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWarehouseData();
  }, [warehouseId, email, navigate]);

  const onPlaceSelected = useCallback((place) => {
    if (!place.geometry || !place.geometry.location) {
      // console.log('Returned place contains no geometry');
      return;
    }

    setSelectedPlace(place);
    const position = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    };
    
    setMarker(position);
    setFormData(prev => ({
      ...prev,
      address: place.formatted_address,
      latitude: position.lat,
      longitude: position.lng
    }));

    // Update form with place details
    const addressComponents = place.address_components;
    let city = '', state = '', country = '', postalCode = '';

    for (const component of addressComponents) {
      const type = component.types[0];
      switch (type) {
        case 'locality':
          city = component.long_name;
          break;
        case 'administrative_area_level_1':
          state = component.long_name;
          break;
        case 'country':
          country = component.long_name;
          break;
        case 'postal_code':
          postalCode = component.long_name;
          break;
        default:
          break;
      }
    }

    setFormData(prev => ({
      ...prev,
      city,
      state,
      country,
      zip: postalCode
    }));
  }, []);

  const onMapClick = useCallback((event) => {
    const position = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    setMarker(position);
    setFormData(prev => ({
      ...prev,
      latitude: position.lat,
      longitude: position.lng
    }));

    // Reverse geocode the clicked location
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: position }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const place = results[0];
        const addressComponents = place.address_components;
        let streetNumber = '', route = '', city = '', state = '', country = '', postalCode = '';

        for (const component of addressComponents) {
          const type = component.types[0];
          switch (type) {
            case 'street_number':
              streetNumber = component.long_name;
              break;
            case 'route':
              route = component.long_name;
              break;
            case 'locality':
              city = component.long_name;
              break;
            case 'administrative_area_level_1':
              state = component.long_name;
              break;
            case 'country':
              country = component.long_name;
              break;
            case 'postal_code':
              postalCode = component.long_name;
              break;
            default:
              break;
          }
        }

        setFormData(prev => ({
          ...prev,
          address: `${streetNumber} ${route}`.trim(),
          city,
          state,
          country,
          zip: postalCode
        }));
      }
    });
  }, []);

  const handleTimeChange = (e) => {
    const { name, value } = e.target;
    // Convert HH:MM to HHMM format directly
    const timeValue = value.replace(':', '');
    setFormData(prev => ({ ...prev, [name]: timeValue }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const validateForm = () => {
    // Check if address was selected from Places API
    if (!selectedPlace) {
      setError('Please select an address from the dropdown suggestions');
      return false;
    }

    // Check required fields
    const requiredFields = [
      'name', 'address', 'city', 'state', 'zip', 'country',
      'operationHoursStart', 'operationHoursEnd', 'pocName', 'pocPhone', 'pocEmail'
    ];

    for (const field of requiredFields) {
      if (!formData[field]) {
        setError(`${field.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`);
        return false;
      }
    }

    // Validate time format
    const timeRegex = /^([0-1][0-9]|2[0-3])[0-5][0-9]$/;
    if (!timeRegex.test(formData.operationHoursStart) || !timeRegex.test(formData.operationHoursEnd)) {
      setError('Invalid time format');
      return false;
    }

    // Validate coordinates
    if (formData.latitude < -90 || formData.latitude > 90) {
      setError('Invalid latitude');
      return false;
    }
    if (formData.longitude < -180 || formData.longitude > 180) {
      setError('Invalid longitude');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Get auth credentials
      const authKey = localStorage.getItem('authKey') || '';
      const userId = localStorage.getItem('userId') || '';
      
      if (!authKey || !userId) {
        throw new Error('Authentication information is missing. Please log in again.');
      }
      
      // Ensure email is set
      if (!formData.email) {
        formData.email = email || localStorage.getItem('userEmail') || '';
      }
      
      // Make sure all required fields are present
      const requiredFields = [
        'name', 'address', 'city', 'state', 'zip', 'country',
        'operationHoursStart', 'operationHoursEnd', 'pocName', 'pocPhone', 'pocEmail',
        'latitude', 'longitude'
      ];
      
      const missingFields = requiredFields.filter(field => !formData[field]);
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }
      
      // Prepare request data with only the fields expected by the backend
      const requestData = {
        name: formData.name,
        auth_key: authKey,
        user_id: userId,
        email: formData.email,
        address: formData.address,
        address_2: formData.address_2 || '',
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country,
        operationHoursStart: formData.operationHoursStart,
        operationHoursEnd: formData.operationHoursEnd,
        pocName: formData.pocName,
        pocPhone: formData.pocPhone,
        pocEmail: formData.pocEmail,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      };

      console.log('Sending update request:', requestData);

      const response = await fetch(`${API_BASE_URL}/brands/warehouses/edit/${warehouseId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const responseData = await response.json();
      console.log('Update response:', responseData);

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to update warehouse');
      }

      // Success - show a brief success message before navigating
      setError('');
      alert('Warehouse updated successfully');
      navigate('/warehouses');
    } catch (error) {
      setError(error.message);
      console.error('Error updating warehouse:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-root">
        <TopMenu pageTitle="Edit Warehouse" email={email} onLogout={onLogout} />
        <div className="dashboard-container">
          <div className="loading-state">
            <p>Loading warehouse data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Edit Warehouse" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="add-warehouse-form-container">
          {error && <div className="error-message">{error}</div>}
          <form onSubmit={handleSubmit} className="add-warehouse-form">
            <div className="form-grid">
              <div className="form-section">
                <h2>Basic Information</h2>
                <div className="form-group">
                  <label htmlFor="name">Warehouse Name*</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter warehouse name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address">Search Location*</label>
                  <Autocomplete
                    onLoad={autocomplete => setSearchBox(autocomplete)}
                    onPlaceChanged={() => {
                      if (searchBox) {
                        const place = searchBox.getPlace();
                        onPlaceSelected(place);
                      }
                    }}
                  >
                    <input
                      type="text"
                      id="address"
                      placeholder="Search and select an address"
                      className="location-search-input"
                      defaultValue={formData.address}
                      required
                    />
                  </Autocomplete>
                  {!selectedPlace && (
                    <div className="input-hint">Please select an address from the dropdown</div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="address_2">Additional Address Details</label>
                  <input
                    type="text"
                    id="address_2"
                    name="address_2"
                    value={formData.address_2}
                    onChange={handleInputChange}
                    placeholder="Apartment, suite, unit, building, floor, etc."
                  />
                </div>

                <div className="map-container">
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={marker || defaultCenter}
                    zoom={marker ? 17 : 5}
                    onClick={onMapClick}
                  >
                    {marker && <Marker position={marker} />}
                  </GoogleMap>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="latitude">Latitude</label>
                    <input
                      type="text"
                      id="latitude"
                      name="latitude"
                      value={formData.latitude}
                      readOnly
                      className="coordinates-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="longitude">Longitude</label>
                    <input
                      type="text"
                      id="longitude"
                      name="longitude"
                      value={formData.longitude}
                      readOnly
                      className="coordinates-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address">Street Address*</label>
                  <input
                    type="text"
                    id="address-field"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter street address"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City*</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter city"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">State*</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter state"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="zip">ZIP Code*</label>
                    <input
                      type="text"
                      id="zip"
                      name="zip"
                      value={formData.zip}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter ZIP code"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="country">Country*</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter country"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h2>Operations Information</h2>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="operationHoursStart">Opening Time (24h)*</label>
                    <input
                      type="time"
                      id="operationHoursStart"
                      name="operationHoursStart"
                      value={formData.operationHoursStart ? 
                        `${formData.operationHoursStart.slice(0, 2)}:${formData.operationHoursStart.slice(2)}` 
                        : ''}
                      onChange={handleTimeChange}
                      required
                      min="00:00"
                      max="23:59"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="operationHoursEnd">Closing Time (24h)*</label>
                    <input
                      type="time"
                      id="operationHoursEnd"
                      name="operationHoursEnd"
                      value={formData.operationHoursEnd ? 
                        `${formData.operationHoursEnd.slice(0, 2)}:${formData.operationHoursEnd.slice(2)}` 
                        : ''}
                      onChange={handleTimeChange}
                      required
                      min="00:00"
                      max="23:59"
                    />
                  </div>
                </div>

                <h2>Point of Contact</h2>
                <div className="form-group">
                  <label htmlFor="pocName">Contact Name*</label>
                  <input
                    type="text"
                    id="pocName"
                    name="pocName"
                    value={formData.pocName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter contact person's name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="pocPhone">Contact Phone*</label>
                  <input
                    type="tel"
                    id="pocPhone"
                    name="pocPhone"
                    value={formData.pocPhone}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter contact phone number"
                    pattern="^\+?[1-9]\d{1,14}$"
                    title="Please enter a valid phone number with country code (e.g., +1-555-123-4567)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="pocEmail">Contact Email*</label>
                  <input
                    type="email"
                    id="pocEmail"
                    name="pocEmail"
                    value={formData.pocEmail}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter contact email"
                  />
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => navigate('/warehouses')}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit"                
                disabled={isSubmitting}
                className="action-button"
              >
                {isSubmitting ? 'Updating Warehouse...' : 'Update Warehouse'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditWarehouse; 